import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import EmptyState from '../../../components/ui/EmptyState';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { getPackageName } from '../../../constants/creditPackages';

const SUPPORT_EMAIL = 'support@angle.dating';

const BillingHistory = ({ userId }) => {
  const [expandedTransaction, setExpandedTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [emailingReceipt, setEmailingReceipt] = useState(null);
  const [emailStatus, setEmailStatus] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    const fetchPurchases = async () => {
      if (!userId) { setIsLoading(false); return; }
      try {
        const { data, error } = await supabase
          ?.from('purchases')
          ?.select('id, amount_paid, credits_purchased, stripe_payment_id, package_name, stripe_receipt_url, status, created_at')
          ?.eq('user_id', userId)
          ?.order('created_at', { ascending: false });

        if (!error && data) {
          setTransactions(data?.map(p => ({
            id: p?.stripe_payment_id ? `TXN-${p?.stripe_payment_id?.slice(-8)?.toUpperCase()}` : `TXN-${p?.id?.slice(-8)?.toUpperCase()}`,
            rawId: p?.id,
            date: p?.created_at,
            package: p?.package_name || getPackageName(p?.credits_purchased),
            credits: p?.credits_purchased,
            amount: parseFloat(p?.amount_paid),
            status: p?.status || 'completed',
            paymentMethod: 'Card on file',
            receiptUrl: p?.stripe_receipt_url || null
          })));
        }
      } catch (err) {
        console.error('Failed to load billing history:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchases();
  }, [userId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadReceipt = (transaction) => {
    if (transaction?.receiptUrl) {
      window.open(transaction?.receiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const receiptContent = `
===========================================
           ANGLE! - PURCHASE RECEIPT
===========================================

Transaction ID: ${transaction?.id}
Date: ${formatDate(transaction?.date)}

-------------------------------------------
ORDER DETAILS
-------------------------------------------
Package: ${transaction?.package}
Credits: ${transaction?.credits}
Amount: $${transaction?.amount?.toFixed(2)}

-------------------------------------------
PAYMENT INFORMATION
-------------------------------------------
Payment Method: ${transaction?.paymentMethod}
Status: ${transaction?.status}

-------------------------------------------
THANK YOU FOR YOUR PURCHASE!
-------------------------------------------
Your credits have been added to your account.

Support: ${SUPPORT_EMAIL}
===========================================
    `;
    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Angle_Receipt_${transaction?.id}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    window.URL?.revokeObjectURL(url);
  };

  const handleEmailReceipt = async (transaction) => {
    const recipientEmail = user?.email;
    if (!recipientEmail) return;

    setEmailingReceipt(transaction?.rawId);
    setEmailStatus(prev => ({ ...prev, [transaction?.rawId]: null }));

    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

      const receiptHtml = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px 20px; background-color: #ffffff;">
          <div style="text-align: center; margin-bottom: 32px;">
            <h1 style="font-size: 28px; font-weight: 700; color: #111827; margin: 0;">Angle!</h1>
            <p style="color: #6B7280; font-size: 14px; margin-top: 4px;">Purchase Receipt</p>
          </div>
          <div style="background: #F9FAFB; border-radius: 12px; padding: 24px; margin-bottom: 24px; border: 1px solid #E5E7EB;">
            <p style="font-size: 12px; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 4px;">Transaction ID</p>
            <p style="font-size: 14px; font-family: monospace; font-weight: 600; color: #111827; margin: 0;">${transaction?.id}</p>
            <p style="font-size: 12px; color: #9CA3AF; margin: 4px 0 0;">${formatDate(transaction?.date)}</p>
          </div>
          <div style="margin-bottom: 24px;">
            <p style="font-size: 12px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px;">Order Details</p>
            <div style="display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #F3F4F6;">
              <span style="color: #374151;">${transaction?.package}</span>
              <span style="font-weight: 600; color: #111827;">$${transaction?.amount?.toFixed(2)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; padding: 12px 0;">
              <span style="color: #6B7280;">Credits</span>
              <span style="color: #374151;">${transaction?.credits} credits</span>
            </div>
          </div>
          <div style="background: #F0FDF4; border-radius: 8px; padding: 12px 16px; margin-bottom: 24px; border: 1px solid #BBF7D0;">
            <p style="color: #15803D; font-size: 14px; font-weight: 600; margin: 0;">✓ ${transaction?.credits} credits added to your account</p>
          </div>
          <div style="border-top: 1px solid #E5E7EB; padding-top: 20px; margin-top: 8px;">
            <p style="font-size: 13px; color: #9CA3AF; margin: 0;">
              Questions? Contact us at <a href="mailto:${SUPPORT_EMAIL}" style="color: #7C3AED;">${SUPPORT_EMAIL}</a>
            </p>
          </div>
        </div>
      `;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          email: recipientEmail,
          type: 'receipt',
          receiptHtml,
          subject: `Your Angle! Receipt — ${transaction?.id}`,
        }),
      });

      if (response?.ok) {
        setEmailStatus(prev => ({ ...prev, [transaction?.rawId]: 'sent' }));
      } else {
        setEmailStatus(prev => ({ ...prev, [transaction?.rawId]: 'error' }));
      }
    } catch (err) {
      console.error('Failed to email receipt:', err);
      setEmailStatus(prev => ({ ...prev, [transaction?.rawId]: 'error' }));
    } finally {
      setEmailingReceipt(null);
    }
  };

  const toggleExpand = (transactionId) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-100 rounded w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)]?.map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-lg" />)}
        </div>
        {[...Array(3)]?.map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg" />)}
      </div>
    );
  }

  if (transactions?.length === 0) {
    return (
      <EmptyState
        icon="Receipt"
        headline="No Billing History"
        explanation="You haven't made any credit purchases yet. Purchase credits to start analyzing your photos."
        ctaText="Purchase Credits"
        onCtaClick={() => window.location.href = '/purchase-credits'}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <Icon name="Receipt" size={20} color="#9333EA" />
          Billing History
        </h2>
        <p className="text-sm text-gray-600">
          View all your credit purchase transactions and download receipts
        </p>
      </div>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="DollarSign" size={16} color="#9333EA" />
            <p className="text-sm font-medium text-purple-900">Total Spent</p>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            ${transactions?.reduce((sum, t) => sum + (t?.amount || 0), 0)?.toFixed(2)}
          </p>
        </div>

        <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Zap" size={16} color="#06B6D4" />
            <p className="text-sm font-medium text-cyan-900">Credits Purchased</p>
          </div>
          <p className="text-2xl font-bold text-cyan-600">
            {transactions?.reduce((sum, t) => sum + (t?.credits || 0), 0)}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="ShoppingBag" size={16} color="#10B981" />
            <p className="text-sm font-medium text-green-900">Transactions</p>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {transactions?.length}
          </p>
        </div>
      </div>
      {/* Transactions List */}
      <div className="space-y-3">
        {transactions?.map((transaction) => (
          <div
            key={transaction?.rawId}
            className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Transaction Header */}
            <div
              className="p-4 bg-white cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
              onClick={() => toggleExpand(transaction?.rawId)}
            >
              <div className="flex items-start sm:items-center justify-between gap-3">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-r from-purple-600 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Icon name="CreditCard" size={20} color="#FFFFFF" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{transaction?.package}</p>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full w-fit">
                        {transaction?.status}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      {formatDate(transaction?.date)} • {transaction?.credits} credits
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                  <p className="text-base sm:text-lg font-bold text-gray-900">
                    ${transaction?.amount?.toFixed(2)}
                  </p>
                  <Icon
                    name={expandedTransaction === transaction?.rawId ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    color="#6B7280"
                  />
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedTransaction === transaction?.rawId && (
              <div className="px-4 pb-4 bg-gray-50 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Transaction ID</p>
                    <p className="text-sm font-mono text-gray-900 break-all">{transaction?.id}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Payment Method</p>
                    <p className="text-sm text-gray-900">{transaction?.paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Credits Purchased</p>
                    <p className="text-sm text-gray-900">{transaction?.credits} credits</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Amount Paid</p>
                    <p className="text-sm text-gray-900">${transaction?.amount?.toFixed(2)}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Download"
                    onClick={() => handleDownloadReceipt(transaction)}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    Download Receipt
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName={emailStatus?.[transaction?.rawId] === 'sent' ? 'CheckCircle' : emailStatus?.[transaction?.rawId] === 'error' ? 'AlertCircle' : 'Mail'}
                    onClick={() => handleEmailReceipt(transaction)}
                    disabled={emailingReceipt === transaction?.rawId}
                    className={`w-full sm:w-auto min-h-[44px] ${emailStatus?.[transaction?.rawId] === 'sent' ? 'text-green-600' : emailStatus?.[transaction?.rawId] === 'error' ? 'text-red-500' : ''}`}
                  >
                    {emailingReceipt === transaction?.rawId
                      ? 'Sending…'
                      : emailStatus?.[transaction?.rawId] === 'sent' ?'Receipt Sent!'
                      : emailStatus?.[transaction?.rawId] === 'error' ?'Failed — Retry' :'Email Receipt'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Icon name="Info" size={20} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">Need help with a transaction?</p>
          <p className="text-sm text-blue-700 mt-1">
            Contact our support team at <a href={`mailto:${SUPPORT_EMAIL}`} className="underline font-medium">{SUPPORT_EMAIL}</a> with your transaction ID for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;